// src/components/StartTTSModal.jsx
// P0.9: Uses XHR to bypass Emergent fetch interceptor that corrupts responses
// P1.1: Production Evidence Request block for actionable failure diagnostics
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Volume2, Clock, AlertCircle, CheckCircle, XCircle, ChevronDown, ChevronUp, Copy, FileText } from 'lucide-react';
import { BACKEND_URL, IS_PRODUCTION } from '@/config/api';
import { xhrFetchAuth, xhrFetchAuthPost, extractErrorMessage, fetchCustodyChain } from '../lib/xhrFetch';

// P1.1: Generate Production Evidence Request markdown
const generateEvidenceReport = (traceId, titleId, statusCode, environment, custodyEvents) => {
  const custodyList = custodyEvents.length > 0
    ? custodyEvents.map((e, i) => `  ${i + 1}. ${e.event}${e.meta?.error_code ? ` (${e.meta.error_code})` : ''}`).join('\n')
    : '  (No custody events retrieved in this environment)';
  
  const envNote = environment === 'PRODUCTION'
    ? 'This failure occurred in PRODUCTION.'
    : `This UI is running in ${environment}. This environment does NOT have access to production data.`;

  return `## Production Evidence Report

### Trace ID
\`${traceId}\`

### Title ID
\`${titleId}\`

### Environment
${environment}

### UI Observation
- TTS start failed with HTTP ${statusCode}
- Custody events visible in this environment:
${custodyList}

### Environment Constraint
${envNote}

---

## Required Production Evidence

To investigate this failure, retrieve the following **from the PRODUCTION environment**:

### 1. Custody Chain (Production DB)
\`\`\`js
db.tts_custody_events
  .find({ trace_id: "${traceId}" })
  .sort({ ts: 1 })
\`\`\`

### 2. Backend Logs (Production)
\`\`\`bash
grep -n "${traceId}" /var/log/supervisor/backend.*.log
\`\`\`

### 3. Production API (if authenticated)
\`\`\`bash
curl "https://www.newaudia.com/api/publisher/titles/${titleId}/tts/custody?trace_id=${traceId}" \\
  -H "Authorization: Bearer <PROD_TOKEN>"
\`\`\`

---

**Blocking Statement:** This trace ID was generated in PRODUCTION. It cannot be retrieved from preview or local environments.
`;
};

// P1.1: Production Evidence Request Component
// P1-C: Enhanced to fetch and display failure summary from evidence endpoint
const ProductionEvidenceRequest = ({ traceId, titleId, statusCode, custodyEvents }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [failureSummary, setFailureSummary] = useState(null);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  
  const environment = IS_PRODUCTION ? 'PRODUCTION' : 'PREVIEW';
  
  // P1-C: Fetch evidence from backend when expanded
  useEffect(() => {
    if (expanded && !failureSummary && traceId) {
      setLoadingEvidence(true);
      xhrFetchAuth(`${BACKEND_URL}/api/admin/tts/evidence?trace_id=${encodeURIComponent(traceId)}`)
        .then(r => {
          if (r.ok && r.json?.failure_summary) {
            setFailureSummary(r.json.failure_summary);
          }
        })
        .catch(e => console.warn('[Evidence] Failed to fetch:', e))
        .finally(() => setLoadingEvidence(false));
    }
  }, [expanded, traceId, failureSummary]);
  
  const evidenceReport = generateEvidenceReport(traceId, titleId, statusCode, environment, custodyEvents);
  
  const handleCopy = async () => {
    try {
      // Include failure summary in copied report if available
      let fullReport = evidenceReport;
      if (failureSummary) {
        fullReport += `\n\n## Failure Summary (from backend)\n`;
        fullReport += `- Subsystem: ${failureSummary.subsystem || 'unknown'}\n`;
        fullReport += `- Exception Type: ${failureSummary.exception_type || 'N/A'}\n`;
        fullReport += `- Exception Message: ${failureSummary.exception_message || 'N/A'}\n`;
        if (failureSummary.stack) {
          fullReport += `\n### Stack Trace\n\`\`\`\n${failureSummary.stack}\n\`\`\`\n`;
        }
      }
      await navigator.clipboard.writeText(fullReport);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className="border-t border-amber-500/30 pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-medium text-amber-300">Production Evidence Request</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-slate-400 hover:text-slate-300"
          >
            {expanded ? 'Hide' : 'Preview'}
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 rounded border border-amber-500/30"
          >
            <Copy className="w-3 h-3" />
            {copied ? 'Copied!' : 'Copy Report'}
          </button>
        </div>
      </div>
      
      {/* P1-C: Show failure summary if available */}
      {expanded && failureSummary && (
        <div className="bg-red-950/30 border border-red-500/20 rounded p-2 mb-2">
          <div className="text-xs font-medium text-red-300 mb-1">Failure Summary</div>
          <div className="text-xs text-slate-400 space-y-1">
            <div><span className="text-slate-500">Subsystem:</span> <span className="text-red-400">{failureSummary.subsystem || 'unknown'}</span></div>
            <div><span className="text-slate-500">Exception:</span> <span className="text-amber-300">{failureSummary.exception_type || 'N/A'}</span></div>
            <div><span className="text-slate-500">Message:</span> {failureSummary.exception_message || 'N/A'}</div>
            {failureSummary.stack && (
              <details className="mt-2">
                <summary className="text-indigo-400 cursor-pointer hover:text-indigo-300">Stack Trace</summary>
                <pre className="mt-1 p-2 bg-slate-950 rounded text-xs text-slate-500 overflow-x-auto max-h-32 overflow-y-auto">
                  {failureSummary.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
      
      {expanded && loadingEvidence && (
        <div className="text-xs text-slate-500 mb-2">Loading evidence...</div>
      )}
      
      {expanded && (
        <div className="bg-slate-950 rounded p-3 max-h-48 overflow-y-auto">
          <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono">{evidenceReport}</pre>
        </div>
      )}
      
      <p className="text-xs text-slate-500 mt-2">
        Copy this report and send to engineering for production investigation.
      </p>
    </div>
  );
};

const StartTTSModal = ({
  isOpen,
  onClose,
  titleId,
  manuscriptData,
  onTTSStarted,
  onTTSComplete,
  forceReset = true,
  redirectOnComplete = true
}) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('idle'); // idle, starting, polling, done, error, scope_mismatch
  const [error, setError] = useState(null);
  const [errorStatusCode, setErrorStatusCode] = useState(null); // P1.1: Track HTTP status for evidence report
  const [scopeMismatchData, setScopeMismatchData] = useState(null); // P1.0: Store mismatch details for reset
  const [resetting, setResetting] = useState(false); // P1.0: Reset in progress
  // PHASE A.3: Custody chain state
  const [custodyChain, setCustodyChain] = useState([]);
  const [custodyExpanded, setCustodyExpanded] = useState(false);
  const [errorTraceId, setErrorTraceId] = useState(null);
  const [progress, setProgress] = useState({
    state: 'IDLE',
    expected_segments: 0,
    completed_segments: 0,
    percent: 0,
    activate_enabled: false,
    activate_reason: ''
  });
  const pollIntervalRef = useRef(null);

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Stop polling when modal closes
  useEffect(() => {
    if (!isOpen && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setPhase('idle');
    }
  }, [isOpen]);

  // P0.9: pollProgress uses xhrFetchAuth - completely bypasses fetch interceptor
  const pollProgress = async () => {
    try {
      // P0.9: xhrFetchAuth returns parsed result using XMLHttpRequest
      const r = await xhrFetchAuth(`${BACKEND_URL}/api/publisher/titles/${titleId}/tts/progress`);
      
      if (!r.ok || r.error) {
        console.warn('[StartTTSModal] Progress poll failed:', r.status, extractErrorMessage(r));
        return;
      }
      
      const data = r.json;
      console.log('[StartTTSModal] Progress:', data);
      
      // Extract from new response contract
      const ttsState = data.tts?.state || 'NONE';
      const completedSegs = data.tts?.completed_segments || 0;
      const totalSegs = data.tts?.total_segments || 0;
      const pct = data.tts?.percent || 0;
      const canActivate = data.activation?.can_activate || false;
      const activationReason = data.activation?.reason || '';
      
      setProgress({
        state: ttsState,
        expected_segments: totalSegs,
        completed_segments: completedSegs,
        percent: pct,
        activate_enabled: canActivate,
        activate_reason: activationReason
      });
      
      // Check if done
      if (canActivate && ttsState === 'DONE') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setPhase('done');
        
        // Notify parent
        onTTSComplete?.(data);
        
        // Auto-redirect after short delay
        if (redirectOnComplete) {
          setTimeout(() => {
            onClose?.();
            navigate(`/publisher/titles/${titleId}/edit`);
          }, 1500);
        }
      } else if (ttsState === 'FAIL' || ttsState === 'BLOCKED') {
        // Stop polling on failure
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setPhase('error');
        setError(data.tts?.fail_reason || data.tts?.blocking_reason || 'TTS failed');
      }
    } catch (err) {
      console.error('[StartTTSModal] Poll error:', err);
    }
  };

  const handleStartTTS = async () => {
    if (phase === 'starting' || phase === 'polling') return;
    
    setPhase('starting');
    setError(null);
    setScopeMismatchData(null);
    
    try {
      // P0.9: Single call to /tts/start using xhrFetchAuthPost
      // Uses XMLHttpRequest to completely bypass fetch interceptor
      console.log('[StartTTSModal] P0.9: Calling /tts/start via XHR...');
      
      // P0.9: xhrFetchAuthPost returns parsed result using XMLHttpRequest
      const r = await xhrFetchAuthPost(
        `${BACKEND_URL}/api/publisher/titles/${titleId}/tts/start`,
        { force_reset: forceReset }
      );
      
      const result = r.json;
      const traceId = r.traceId || result?.trace_id || 'unknown';
      console.log('[StartTTSModal] P0.9: TTS start response:', result, 'status:', r.status, 'trace_id:', traceId);
      
      // Handle internal parsing errors first
      if (r.error) {
        console.error('[StartTTSModal] P0.9: Response parsing error:', r.error);
        setErrorTraceId(traceId);
        setErrorStatusCode(r.status || 0); // P1.1: Track status
        setError(`[${r.status}] ${r.error.message || 'Failed to parse response'}`);
        setPhase('error');
        
        // Still try to fetch custody chain
        if (traceId && traceId !== 'unknown') {
          fetchCustodyChain(titleId, traceId, BACKEND_URL)
            .then(custodyResult => {
              if (custodyResult.ok && custodyResult.events.length > 0) {
                setCustodyChain(custodyResult.events);
              }
            })
            .catch(e => console.warn('[StartTTSModal] Failed to fetch custody:', e));
        }
        return;
      }
      
      // P0.9: Handle HTTP errors
      if (!r.ok) {
        const errorDetail = result?.detail || result;
        const errorCode = errorDetail?.error || (typeof errorDetail?.error === 'object' ? errorDetail.error.code : 'UNKNOWN');
        
        // P0.9: Construct user-facing error with status and backend message
        const backendMessage = extractErrorMessage(r);
        console.error('[StartTTSModal] P0.9 Error:', {
          trace_id: traceId,
          status: r.status,
          error_code: errorCode,
          message: backendMessage,
          raw_text: r.rawText?.substring(0, 500)
        });
        
        // P1.1: Store status code for evidence report
        setErrorStatusCode(r.status);
        
        // P0.9: Store trace_id and fetch custody chain automatically
        setErrorTraceId(traceId);
        if (traceId && traceId !== 'unknown') {
          fetchCustodyChain(titleId, traceId, BACKEND_URL)
            .then(custodyResult => {
              if (custodyResult.ok && custodyResult.events.length > 0) {
                setCustodyChain(custodyResult.events);
                console.log('[StartTTSModal] P0.9: Fetched custody chain:', custodyResult.events);
              } else if (custodyResult.events.length === 0) {
                console.log('[StartTTSModal] P0.9: No custody events found for trace_id');
              }
            })
            .catch(e => console.warn('[StartTTSModal] Failed to fetch custody chain:', e));
        }
        
        // P1-HOTFIX: Handle TTS_ALREADY_RUNNING - no retry needed
        if (errorCode === 'TTS_ALREADY_RUNNING') {
          setError(`✓ Audio generation is already in progress.\n\nNo action required. The process will complete automatically.`);
          setPhase('error');  // Shows as info, not error
          return;
        }
        
        if (errorCode === 'OUTLINE_COMMIT_REQUIRED') {
          setError(`⚠️ Outline must be committed before generating audio.\n\n${errorDetail?.message || backendMessage}\n\nOpen Outline Editor and click "Save Draft" or "Save and Continue".`);
          setPhase('error');
          return;
        }
        
        if (errorCode === 'RESET_FAILED') {
          setError(`⚠️ Audio start failed due to reset error.\n\n${errorDetail?.message || backendMessage}\n\nNo changes were committed. Please retry.`);
          setPhase('error');
          return;
        }
        
        // P0.9: Show HTTP status + real backend message (trace shown separately in diagnostics)
        setError(`[${r.status}] ${backendMessage}`);
        setPhase('error');
        return;
      }
      
      if (result?.ok) {
        // P0.9: Success - notify parent and start polling
        console.log(`[StartTTSModal] P0.9: ✅ Started job ${result.job_id} with ${result.total_segments} segments (trace: ${traceId})`);
        
        onTTSStarted?.(result);
        setPhase('polling');
        
        // Initialize progress from start response
        setProgress(prev => ({
          ...prev,
          state: result.state || 'QUEUED',
          expected_segments: result.total_segments || result.segments || manuscriptData?.segment_count || 0
        }));
        
        // Start polling every 2 seconds
        pollIntervalRef.current = setInterval(pollProgress, 2000);
        
        // Also poll immediately
        pollProgress();
      } else {
        // P0.9: Response was ok=true but result.ok=false - show the message
        setErrorTraceId(traceId);
        setError(`${result?.message || 'TTS start failed'}`);
        setPhase('error');
      }
      
    } catch (err) {
      console.error('[StartTTSModal] P0.9: Error starting TTS:', err);
      setError(`${err.message || 'Failed to start audio generation'}\n\n(Network or client error)`);
      setPhase('error');
    }
  };

  const handleNotNow = () => {
    if (phase === 'polling') {
      // Stop polling but don't close - TTS is running
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
    onClose?.();
  };

  const handleRetry = () => {
    setPhase('idle');
    setError(null);
    setErrorStatusCode(null); // P1.1: Clear status
    setErrorTraceId(null); // Clear trace
    setCustodyChain([]); // Clear custody
    setScopeMismatchData(null);
  };

  // Calculate estimated time
  const segmentCount = progress.expected_segments || manuscriptData?.segment_count || manuscriptData?.chapter_count || 0;
  const estimatedMinutes = Math.max(5, Math.ceil(segmentCount * 0.5));

  // State label
  const getStateLabel = () => {
    switch (progress.state) {
      case 'QUEUED': return 'Queued';
      case 'EXECUTING': return 'Generating Audio';
      case 'FINALIZING': return 'Finalizing';
      case 'DONE': return 'Complete';
      case 'FAIL': return 'Failed';
      case 'BLOCKED': return 'Blocked';
      default: return progress.state;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleNotNow()}>
      <DialogContent 
        className="bg-slate-900 border-slate-700 text-slate-50 sm:max-w-md"
        data-testid="start-tts-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              phase === 'done' ? 'bg-emerald-600/20' :
              phase === 'error' ? 'bg-red-600/20' :
              phase === 'scope_mismatch' ? 'bg-amber-600/20' :
              'bg-indigo-600/20'
            }`}>
              {phase === 'done' ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : phase === 'error' ? (
                <XCircle className="w-5 h-5 text-red-400" />
              ) : phase === 'scope_mismatch' ? (
                <AlertCircle className="w-5 h-5 text-amber-400" />
              ) : (
                <Volume2 className="w-5 h-5 text-indigo-400" />
              )}
            </div>
            {phase === 'done' ? 'Audio Ready!' :
             phase === 'error' ? 'Generation Failed' :
             phase === 'scope_mismatch' ? 'Outline Changed' :
             phase === 'polling' ? 'Generating Audio...' :
             'Ready to Generate Audio'}
          </DialogTitle>
          <DialogDescription className="text-slate-400 pt-2">
            {phase === 'done' ? 'Your audiobook is ready. You can now activate it for readers.' :
             phase === 'error' ? 'There was a problem generating your audio.' :
             phase === 'polling' ? 'Please wait while we generate your audiobook.' :
             'Your manuscript has been validated and is ready for audio generation.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* P1.1: No more scope_mismatch phase - /tts/start handles reset automatically */}
          
          {/* Progress display (polling phase) */}
          {(phase === 'polling' || phase === 'done') && (
            <div className="space-y-3">
              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">{getStateLabel()}</span>
                  <span className="text-slate-200 font-mono">
                    {progress.completed_segments}/{progress.expected_segments}
                  </span>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      phase === 'done' ? 'bg-emerald-500' : 'bg-indigo-500'
                    }`}
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <div className="text-right text-sm text-slate-500">
                  {progress.percent}%
                </div>
              </div>
              
              {/* Status indicator */}
              {phase === 'polling' && (
                <div className="flex items-center justify-center gap-2 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Processing segments...</span>
                </div>
              )}
            </div>
          )}

          {/* Manuscript info (idle phase) */}
          {phase === 'idle' && manuscriptData && (
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Segments</span>
                <span className="text-slate-200 font-medium">{segmentCount}</span>
              </div>
              {manuscriptData.word_count && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Words</span>
                  <span className="text-slate-200 font-medium">
                    {manuscriptData.word_count.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Estimated time
                </span>
                <span className="text-slate-200 font-medium">
                  {estimatedMinutes < 60 
                    ? `${estimatedMinutes} minutes`
                    : `${Math.floor(estimatedMinutes / 60)}h ${estimatedMinutes % 60}m`
                  }
                </span>
              </div>
            </div>
          )}

          {/* Info text */}
          {phase === 'idle' && (
            <p className="text-sm text-slate-400">
              Audio generation begins when you click <strong className="text-slate-200">Start TTS Now</strong>.
              You can safely close your browser during processing.
            </p>
          )}

          {/* Done message */}
          {phase === 'done' && (
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
              <p className="text-sm text-emerald-300">
                All {progress.expected_segments} segments generated successfully!
                Redirecting to title page...
              </p>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-300 whitespace-pre-wrap">{error}</p>
              </div>
              
              {/* PHASE A.3: Trace ID and Custody Chain Diagnostics */}
              {errorTraceId && (
                <div className="border-t border-red-500/20 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Trace: {errorTraceId}</span>
                    {custodyChain.length > 0 && (
                      <button
                        onClick={() => setCustodyExpanded(!custodyExpanded)}
                        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                      >
                        {custodyExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {custodyExpanded ? 'Hide' : 'Show'} Diagnostics ({custodyChain.length})
                      </button>
                    )}
                  </div>
                  
                  {/* Custody Chain (collapsible) */}
                  {custodyExpanded && custodyChain.length > 0 && (
                    <div className="bg-slate-900/50 rounded p-2 max-h-40 overflow-y-auto">
                      <div className="space-y-1 font-mono text-xs">
                        {custodyChain.map((evt, idx) => (
                          <div key={idx} className="flex gap-2 text-slate-400">
                            <span className="text-slate-600">{idx + 1}.</span>
                            <span className={
                              evt.event?.includes('fail') || evt.event?.includes('error')
                                ? 'text-red-400'
                                : evt.event?.includes('ok') || evt.event?.includes('success')
                                ? 'text-emerald-400'
                                : 'text-slate-400'
                            }>
                              {evt.event}
                            </span>
                            {evt.segment_count !== undefined && (
                              <span className="text-slate-500">segments={evt.segment_count}</span>
                            )}
                            {evt.error && (
                              <span className="text-red-400 truncate" title={evt.error}>
                                error={evt.error.substring(0, 50)}...
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* P1.1: Production Evidence Request */}
              {errorTraceId && (
                <ProductionEvidenceRequest
                  traceId={errorTraceId}
                  titleId={titleId}
                  statusCode={errorStatusCode || 0}
                  custodyEvents={custodyChain}
                />
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {phase === 'idle' && (
            <>
              <Button
                variant="outline"
                onClick={handleNotNow}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="start-tts-not-now-btn"
              >
                Not Now
              </Button>
              <Button
                onClick={handleStartTTS}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold"
                data-testid="start-tts-now-btn"
              >
                <Volume2 className="w-4 h-4 mr-2" />
                Start TTS Now
              </Button>
            </>
          )}
          
          {phase === 'starting' && (
            <Button disabled className="bg-indigo-600">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Starting...
            </Button>
          )}
          
          {phase === 'polling' && (
            <Button
              variant="outline"
              onClick={handleNotNow}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Close (TTS continues)
            </Button>
          )}
          
          {phase === 'done' && (
            <Button
              onClick={() => navigate(`/publisher/titles/${titleId}/edit`)}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Go to Title
            </Button>
          )}
          
          {phase === 'error' && (
            <>
              <Button
                variant="outline"
                onClick={handleNotNow}
                className="border-slate-600"
              >
                Close
              </Button>
              <Button
                onClick={handleRetry}
                className="bg-indigo-600 hover:bg-indigo-500"
              >
                Try Again
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StartTTSModal;
